import { insideTelegram } from './env';

/**
 * Безопасные зоны клиента: вырез устройства (safeAreaInset) и полоса с
 * кнопками Telegram в полноэкранном режиме (contentSafeAreaInset: «Закрыть»
 * слева, «⌄ ⋯» справа). Без них шапка приложения уезжает под эти кнопки.
 *
 * Раньше переменные ставил bindViewportCssVars из SDK, но он вызывался до
 * окончания асинхронного mountViewport() и молча не срабатывал. Здесь читаем
 * Telegram.WebApp напрямую (telegram-web-app.js подключён в index.html) и
 * обновляем по событиям: значения приходят после requestFullscreen.
 */
interface Insets {
  top?: number;
  bottom?: number;
}

interface TelegramWebApp {
  safeAreaInset?: Insets;
  contentSafeAreaInset?: Insets;
  isFullscreen?: boolean;
  onEvent?: (event: string, handler: () => void) => void;
}

function webApp(): TelegramWebApp | undefined {
  return (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp;
}

/**
 * Запас, если клиент в полноэкранном режиме не сообщил высоту своей полосы
 * (старые сборки): кнопки Telegram выше 46px не бывают.
 */
const FULLSCREEN_FALLBACK = 46;

let subscribed = false;

/** Безопасно вызывать повторно: подписка на события ставится один раз. */
export function bindSafeArea(): void {
  if (!insideTelegram()) return;
  const app = webApp();
  if (!app) return;

  const apply = (): void => {
    const style = document.documentElement.style;
    const safe = app.safeAreaInset ?? {};
    const content = app.contentSafeAreaInset ?? {};
    let contentTop = content.top ?? 0;
    if (app.isFullscreen && contentTop === 0) contentTop = FULLSCREEN_FALLBACK;
    style.setProperty('--tg-viewport-safe-area-inset-top', `${safe.top ?? 0}px`);
    style.setProperty('--tg-viewport-safe-area-inset-bottom', `${safe.bottom ?? 0}px`);
    style.setProperty('--tg-viewport-content-safe-area-inset-top', `${contentTop}px`);
    style.setProperty('--tg-viewport-content-safe-area-inset-bottom', `${content.bottom ?? 0}px`);
    document.documentElement.classList.toggle('tg-fullscreen', app.isFullscreen === true);
  };

  apply();
  if (subscribed) return;
  subscribed = true;
  for (const event of ['safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged', 'viewportChanged']) {
    app.onEvent?.(event, apply);
  }
}